<template>
    <nav v-if="pageCount > 1" :class="c.nav" :aria-label="translate('agent-search.paging.label')">
        <ul :class="c.list">
            <li :class="[c.item, { [c.itemDisabled]: page <= 1 }]">
                <a
                    v-if="page > 1"
                    :href="hrefFor(page - 1)"
                    :class="[c.link, c.linkPrev]"
                    :aria-label="translate('agent-search.paging.prev')"
                    @click="navigate($event, page - 1)"
                >‹</a>
                <span v-else :class="[c.link, c.linkPrev, c.linkDisabled]" aria-hidden="true">‹</span>
            </li>
            <li
                v-for="(entry, index) in entries"
                :key="`${entry}-${index}`"
                :class="[c.item, { [c.itemActive]: entry === page, [c.itemDisabled]: entry === 'gap' }]"
            >
                <span v-if="entry === 'gap'" :class="c.gap" aria-hidden="true">…</span>
                <!-- a link, so aria-label is allowed and aria-current is announced on the element in focus -->
                <a
                    v-else-if="entry === page"
                    :href="hrefFor(entry)"
                    :class="[c.link, c.linkCurrent]"
                    aria-current="page"
                    :aria-label="labelFor(entry)"
                    @click.prevent
                >{{ entry }}</a>
                <a
                    v-else
                    :href="hrefFor(entry)"
                    :class="c.link"
                    :aria-label="labelFor(entry)"
                    @click="navigate($event, entry)"
                >{{ entry }}</a>
            </li>
            <li :class="[c.item, { [c.itemDisabled]: page >= pageCount }]">
                <a
                    v-if="page < pageCount"
                    :href="hrefFor(page + 1)"
                    :class="[c.link, c.linkNext]"
                    :aria-label="translate('agent-search.paging.next')"
                    @click="navigate($event, page + 1)"
                >›</a>
                <span v-else :class="[c.link, c.linkNext, c.linkDisabled]" aria-hidden="true">›</span>
            </li>
        </ul>
    </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useTranslate } from './composables/useTranslate'
import { useClasses } from './composables/useClasses'
import { buildSearchQuery, pageWindow } from '../core/url'

const props = defineProps<{
    page: number
    pageCount: number
    query: string
    /** `location.search` of the page, so the hrefs keep unrelated parameters. */
    currentSearch: string
    /** Total result count of the block, for overrides that show it next to the pages. */
    total?: number
    /** `total` is exact (last page seen); otherwise Google's estimate. */
    exact?: boolean
}>()

const emit = defineEmits<{
    change: [page: number]
}>()

const translate = useTranslate()
const classes = useClasses()
const c = computed(() => classes.value.pagination)

const entries = computed(() => pageWindow(props.page, props.pageCount))

function hrefFor(page: number): string {
    return buildSearchQuery(props.currentSearch, { q: props.query, page }) || '?'
}

/** Pages in place; modifier and middle clicks keep the browser's default (open in a new tab or window). */
function navigate(event: MouseEvent, target: number): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return
    }

    event.preventDefault()
    emit('change', target)
}

function labelFor(page: number): string {
    return translate('agent-search.paging.page', { page })
}
</script>
